'use client';

import { apiClient } from '@/lib/api-client';
import {
  Organization,
  PetrohqApiResponseListOrganization,
} from '@/lib/api/data-contracts';
import { createContext, useCallback, useContext, useState } from 'react';

interface OrganizationContextType {
  organizations: Organization[];
  isLoading: boolean;
  error: string | null;
  loadOrganizations: () => Promise<void>;
  getCompaniesByType: () => Organization[];
  getOrganizationsByCompany: (companyCode: string) => Organization[];
  getBusinessesByOrganization: (organizationCode: string) => Organization[];
  getStoresByBusiness: (businessCode: string) => Organization[];
  getStoresByOrganization: (organizationCode: string) => Organization[];
  getAllStores: () => string[];
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(
  undefined
);

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error(
      'useOrganization must be used within an OrganizationProvider'
    );
  }
  return context;
}

interface OrganizationProviderProps {
  children: React.ReactNode;
}

export function OrganizationProvider({ children }: OrganizationProviderProps) {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadOrganizations = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.get('common/organization');
      const data: PetrohqApiResponseListOrganization = await response.json();

      if (data.result) {
        setOrganizations(data.result);
      } else {
        setError('조직정보를 불러올 수 없습니다.');
      }
    } catch (err) {
      console.error('Organization loading error:', err);
      setError('조직정보 로딩 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 회사 목록 조회
  const getCompaniesByType = useCallback((): Organization[] => {
    return organizations.filter(
      org => org.type === 'COMPANY' && org.compCd && org.orgName
    );
  }, [organizations]);

  // 특정 회사의 조직 목록 조회
  const getOrganizationsByCompany = useCallback(
    (companyCode: string): Organization[] => {
      return organizations.filter(
        org =>
          org.type === 'ORGANIZATION' &&
          org.compCd === companyCode &&
          org.orgCd &&
          org.orgName
      );
    },
    [organizations]
  );

  // 특정 조직의 영업지사 목록 조회
  const getBusinessesByOrganization = useCallback(
    (organizationCode: string): Organization[] => {
      return organizations.filter(
        org =>
          org.type === 'BUSINESS' &&
          org.parentOrgCd === organizationCode &&
          org.orgCd &&
          org.orgName
      );
    },
    [organizations]
  );

  // 특정 영업지사의 주유소 목록 조회
  const getStoresByBusiness = useCallback(
    (businessCode: string): Organization[] => {
      return organizations.filter(
        org =>
          org.type === 'STORE' &&
          org.parentOrgCd === businessCode &&
          org.orgCd &&
          org.orgName
      );
    },
    [organizations]
  );

  // 특정 조직 하위의 모든 주유소 조회 (영업지사를 거쳐서)
  const getStoresByOrganization = useCallback(
    (organizationCode: string): Organization[] => {
      // 먼저 해당 조직의 영업지사들을 찾음
      const businesses = getBusinessesByOrganization(organizationCode);

      // 각 영업지사의 주유소들을 모두 수집
      const stores: Organization[] = [];
      businesses.forEach(business => {
        const businessStores = getStoresByBusiness(business.orgCd!);
        stores.push(...businessStores);
      });

      // 조직 직하의 주유소도 포함 (영업지사를 거치지 않는 경우)
      const directStores = organizations.filter(
        org =>
          org.type === 'STORE' &&
          org.parentOrgCd === organizationCode &&
          org.orgCd &&
          org.orgName
      );
      stores.push(...directStores);

      return stores;
    },
    [organizations, getBusinessesByOrganization, getStoresByBusiness]
  );

  // 모든 store 코드 조회
  const getAllStores = useCallback((): string[] => {
    return organizations
      .filter(org => org.type === 'STORE' && org.orgCd && org.orgName)
      .map(org => org.orgCd!);
  }, [organizations]);

  const value: OrganizationContextType = {
    organizations,
    isLoading,
    error,
    loadOrganizations,
    getCompaniesByType,
    getOrganizationsByCompany,
    getBusinessesByOrganization,
    getStoresByBusiness,
    getStoresByOrganization,
    getAllStores,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}
